import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid2, IconButton, Stack } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import React, { useCallback, useContext, useRef, useState } from "react";
import Panel from "../../../common/components/Panel";
import Spinner from "../../../common/components/Spinner";
import getNestedPropertyValue from "../../../common/getNestedPropertyValue";
import { KeyNames, useClickAway, useErrorHandler, useKeyDown } from "../../../common/hooks";
import BundleContext from "../../Contexts/BundleContext";
import useBulkEdit from "../../Hooks/useBulkEdit";
import { useComponentSelection } from "../../Hooks/useComponentSelection";
import useMaskPiiSetting from "../../Hooks/useMaskPiiSetting";
import useObservedExpression from "../../Hooks/useObservedExpression";
import { DataDisplayEditMode, DataDisplayModel } from "../../Models/DataDisplayModel";
import { IHasLayout } from "../../Models/IHasLayout";
import { PropertyDisplayFormat } from "../../Models/PropertyDisplayFormat";
import { PropertyDisplayModel } from "../../Models/PropertyDisplayModel";
import { formatTemplateForDisplay } from "../../Utils/piiMasking";
import IComponentProps from "../IComponentProps";
import PanelAction from "../Panel/PanelAction";
import getComponentLayout, { normalizeLayoutValue } from "../Table/getComponentLayout";
import convertValueForSave from "./convertValueForSave";
import DataDisplayPropertyLabel from "./DataDisplayPropertyLabel";
import DisplayPropertyValue from "./DataDisplayPropertyValue";
import formatPropertyValue from "./formatPropertyValue";
import { handleLookupFieldSave, updateContextAndRefresh } from "./handleSaveEditHelpers";
import hasPropertyValueChanged from "./hasPropertyValueChanged";

const DATA_DISPLAY_PROPERTIES_PATH_SEGMENT = "properties";

type DataDisplayPropertyItemProps = {
  component: DataDisplayModel;
  componentPath: string[];
  property: PropertyDisplayModel;
  propertyIndex: number;
  rowDirection?: boolean;
  editingPropertyIndex: number | null;
  children: React.ReactNode;
};

const DataDisplayPropertyItem: React.FC<DataDisplayPropertyItemProps> = ({
  component,
  componentPath,
  property,
  propertyIndex,
  rowDirection,
  editingPropertyIndex,
  children,
}) => {
  const elementSx = property.Layout ? getComponentLayout(property) : {};
  const width = elementSx.width;
  const flexBasis = normalizeLayoutValue(width);
  const hasExplicitWidth = flexBasis !== undefined;
  const selectionProps = useComponentSelection({
    component: {
      Id: `${component.Id}-property-${propertyIndex}`,
      Type: "DataDisplayProperty",
      Layout: {},
    },
    componentPath: [...componentPath, DATA_DISPLAY_PROPERTIES_PATH_SEGMENT, String(propertyIndex)],
    isContainer: false,
  });

  return (
    <Grid2
      key={propertyIndex}
      size={property.Layout?.Size}
      sx={{
        ...elementSx,
        display: "flex",
        alignItems: rowDirection ? "stretch" : "center",
        flex: elementSx.flex === undefined && hasExplicitWidth ? `0 0 ${flexBasis}` : elementSx.flex,
        flexBasis: elementSx.flex === undefined && hasExplicitWidth ? flexBasis : elementSx.flexBasis,
        flexShrink: elementSx.flex === undefined && hasExplicitWidth ? 0 : elementSx.flexShrink,
        width: hasExplicitWidth ? flexBasis : elementSx.width,
        minWidth: elementSx.minWidth ?? (hasExplicitWidth ? flexBasis : undefined),
        maxWidth: elementSx.maxWidth ?? (hasExplicitWidth ? flexBasis : undefined),
        overflow: "visible",
        "& .MuiIconButton-root": {
          visibility: editingPropertyIndex === propertyIndex ? "visible" : "hidden",
        },
        "&:hover .MuiIconButton-root": {
          visibility: "visible",
        },
      }}
      {...selectionProps}
    >
      {children}
    </Grid2>
  );
};

const DataDisplayComponent: React.FC<IComponentProps<DataDisplayModel>> = ({ component, context, componentPath = [] }) => {
  const errorHandler = useErrorHandler();
  const bundleContext = useContext(BundleContext);
  const phoneNumberFormat = bundleContext.Settings?.PhoneNumberFormat;
  const currencyCode = bundleContext.Settings?.CurrencyCode ?? "USD";
  const [maskPii] = useMaskPiiSetting();

  const [data, dataPending, dataRefresh] = useObservedExpression<any>(component.GetData, context, "DataDisplayComponent");

  const [editingPropertyIndex, setEditingPropertyIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState<string | Dayjs | number | null>("");
  const [originalValue, setOriginalValue] = useState<string | Dayjs | number | null>("");
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const isDialogEditMode = component.EditMode === DataDisplayEditMode.Dialog;

  const selectionProps = useComponentSelection({
    component,
    componentPath,
    isContainer: false,
  });

  // Bulk edit mode hook
  const {
    isBulkEditMode,
    bulkEditValues,
    originalBulkValues,
    hasBulkChanges,
    handleEnterBulkEditMode,
    handleCancelBulkEdit,
    handleSaveBulkEdit,
    handleBulkEditChange,
  } = useBulkEdit({
    component,
    data,
    context,
    errorHandler,
    dataRefresh,
    setIsUpdating,
    phoneNumberFormat,
  });

  const handleOpenBulkEditMode = useCallback(() => {
    if (!component.UpdateData || !data) {
      return;
    }

    handleEnterBulkEditMode();
    if (isDialogEditMode) {
      setIsEditDialogOpen(true);
    }
  }, [component.UpdateData, data, handleEnterBulkEditMode, isDialogEditMode]);

  const handleCancelDialogEdit = useCallback(() => {
    handleCancelBulkEdit();
    setIsEditDialogOpen(false);
  }, [handleCancelBulkEdit]);

  const handleSaveDialogEdit = useCallback(async () => {
    await handleSaveBulkEdit();
    setIsEditDialogOpen(false);
  }, [handleSaveBulkEdit]);

  const handleDialogClose = useCallback(
    (_event: object, reason: "backdropClick" | "escapeKeyDown") => {
      if (isUpdating) {
        return;
      }

      if (reason === "backdropClick" || reason === "escapeKeyDown") {
        handleCancelDialogEdit();
      }
    },
    [handleCancelDialogEdit, isUpdating]
  );

  const handleEnterEditMode = useCallback(
    async (propertyIndex: number, property: PropertyDisplayModel) => {
      if (!property.UpdateData && !property.Lookup) return;
      if (property.IsReadOnly) return;

      const currentValue = formatPropertyValue(data, property, undefined, undefined, phoneNumberFormat, currencyCode);

      // Parse date/datetime values to Dayjs
      let editVal: string | Dayjs | number | null = currentValue;
      if (property.Format === PropertyDisplayFormat.Date || property.Format === PropertyDisplayFormat.DateTime) {
        const fieldValue = property.Field ? getNestedPropertyValue(property.Field, data) : null;
        editVal = fieldValue ? dayjs(fieldValue) : null;
      } else if (property.Format === PropertyDisplayFormat.Currency) {
        // Use raw numeric value for currency editing
        const fieldValue = property.Field ? getNestedPropertyValue(property.Field, data) : null;
        editVal = fieldValue !== null && fieldValue !== undefined ? fieldValue : "";
      } else if (property.Format === PropertyDisplayFormat.Percent) {
        // Use raw value as-is for editing
        const fieldValue = property.Field ? getNestedPropertyValue(property.Field, data) : null;
        editVal = fieldValue !== null && fieldValue !== undefined ? parseFloat(fieldValue) : "";
      } else if (property.Lookup) {
        // For lookup fields, load the current item before entering edit mode
        editVal = null;
        if (property.Lookup.Item) {
          try {
            const item = await context.Evaluator.evaluate(property.Lookup.Item, context);
            if (item) {
              editVal = item;
            }
          } catch (error) {
            errorHandler(error);
          }
        }
      }

      setEditValue(editVal);
      setOriginalValue(editVal);
      setEditingPropertyIndex(propertyIndex);
    },
    [data, context, errorHandler, phoneNumberFormat, currencyCode]
  );

  const handleCancelEdit = useCallback(() => {
    setEditingPropertyIndex(null);
    setEditValue("");
    setOriginalValue("");
  }, []);

  const containerRef = useRef<HTMLDivElement>(null);

  // Escape: exit individual edit mode when no changes made
  useKeyDown(
    [KeyNames.Escape],
    useCallback(() => {
      if (editingPropertyIndex !== null) {
        const editedProp = component.Properties[editingPropertyIndex];
        const changed = hasPropertyValueChanged(editValue, originalValue, editedProp?.Format);
        if (!changed) handleCancelEdit();
      } else if (isBulkEditMode && !hasBulkChanges) {
        if (isDialogEditMode) {
          handleCancelDialogEdit();
        } else {
          handleCancelBulkEdit();
        }
      }
    }, [
      editingPropertyIndex,
      editValue,
      originalValue,
      component.Properties,
      isBulkEditMode,
      hasBulkChanges,
      isDialogEditMode,
      handleCancelEdit,
      handleCancelBulkEdit,
      handleCancelDialogEdit,
    ]),
    (editingPropertyIndex !== null || (isBulkEditMode && !isDialogEditMode)) && !isUpdating
  );

  // Click-away: exit edit/bulk-edit mode when clicking outside the component
  useClickAway(
    containerRef,
    useCallback(() => {
      if (editingPropertyIndex !== null) {
        const editedProp = component.Properties[editingPropertyIndex];
        const changed = hasPropertyValueChanged(editValue, originalValue, editedProp?.Format);
        if (!changed) handleCancelEdit();
      } else if (isBulkEditMode && !hasBulkChanges) {
        if (isDialogEditMode) {
          handleCancelDialogEdit();
        } else {
          handleCancelBulkEdit();
        }
      }
    }, [
      editingPropertyIndex,
      editValue,
      originalValue,
      component.Properties,
      isBulkEditMode,
      hasBulkChanges,
      isDialogEditMode,
      handleCancelEdit,
      handleCancelBulkEdit,
      handleCancelDialogEdit,
    ]),
    (editingPropertyIndex !== null || (isBulkEditMode && !isDialogEditMode)) && !isUpdating
  );

  const handleSaveEdit = useCallback(
    async (property: PropertyDisplayModel) => {
      if (!property.UpdateData && !property.Lookup) return;

      setIsUpdating(true);
      try {
        const eventContext = context.CreateChild();
        let valueToSend = convertValueForSave(editValue, property.Format);

        // Handle lookup fields
        if (property.Lookup) {
          valueToSend = await handleLookupFieldSave(editValue, property.Lookup, context, eventContext);
        }

        // Update context and refresh data
        await updateContextAndRefresh(
          property.UpdateData,
          valueToSend,
          component.GetData,
          property.Field,
          !!property.Lookup,
          context,
          eventContext,
          dataRefresh
        );

        // Exit edit mode
        setEditingPropertyIndex(null);
        setEditValue("");
        setOriginalValue("");
      } catch (error) {
        errorHandler(error);
      } finally {
        setIsUpdating(false);
      }
    },
    [editValue, context, errorHandler, dataRefresh, component]
  );

  const componentLayoutSx = getComponentLayout(component);
  const headerLayoutSx = component.HeaderLayout ? getComponentLayout({ Layout: component.HeaderLayout } as IHasLayout) : undefined;

  const headerLabel =
    component.HeaderLabel && data
      ? formatTemplateForDisplay(component.HeaderLabel, data, {
          enabled: maskPii,
        })
      : component.HeaderLabel;
  const headerActionSize = component.Compact ? "small" : "medium";
  const isInlineBulkEditMode = isBulkEditMode && !isDialogEditMode;

  // Build header actions
  const headerActions = (
    <>
      {component.UpdateData && !isBulkEditMode && (
        <IconButton size={headerActionSize} onClick={handleOpenBulkEditMode} disabled={!data || dataPending}>
          <EditIcon fontSize={headerActionSize} />
        </IconButton>
      )}
      {isInlineBulkEditMode && (
        <>
          <IconButton size={headerActionSize} onClick={handleSaveBulkEdit} color="primary" disabled={!hasBulkChanges || isUpdating} loading={isUpdating}>
            <CheckIcon fontSize={headerActionSize} />
          </IconButton>
          <IconButton size={headerActionSize} onClick={handleCancelBulkEdit} disabled={isUpdating}>
            <CloseIcon fontSize={headerActionSize} />
          </IconButton>
        </>
      )}
      {component.HeaderActions && (
        <PanelAction
          actions={component.HeaderActions}
          size={headerActionSize}
          formatData={data}
          context={context}
          ownerComponentId={component.Id}
          ownerComponentPath={componentPath}
        />
      )}
    </>
  );

  const renderProperties = (renderInDialog: boolean) => (
    <Grid2 container direction={component.RowDirection ? "row" : "column"} spacing={component.RowDirection ? 2 : 0.5} sx={{ overflow: "visible" }}>
      {component.Properties.map((prop, index) => {
        const isBulkProp = isBulkEditMode && !prop.UpdateData && !prop.Lookup;
        const isBulkEditable = isBulkProp && !prop.IsReadOnly && (renderInDialog ? isDialogEditMode : !isDialogEditMode);
        return (
          <DataDisplayPropertyItem
            key={index}
            component={component}
            componentPath={componentPath}
            property={prop}
            propertyIndex={index}
            rowDirection={component.RowDirection}
            editingPropertyIndex={renderInDialog ? null : editingPropertyIndex}
          >
            <Stack
              direction={!component.RowDirection ? "row" : "column"}
              spacing={!component.RowDirection ? 1 : 0.5}
              alignItems={!component.RowDirection ? "center" : "flex-start"}
              sx={{ width: "100%", minWidth: 0 }}
            >
              <DataDisplayPropertyLabel property={prop} context={context} rowDirection={component.RowDirection} />
              <DisplayPropertyValue
                key={prop.Field ?? String(index)}
                data={data}
                property={prop}
                context={context}
                phoneNumberFormat={phoneNumberFormat}
                currencyCode={currencyCode}
                isRowDirection={component.RowDirection ?? false}
                isEditing={isBulkEditable ? true : !renderInDialog && editingPropertyIndex === index}
                editValue={isBulkEditable && prop.Field ? bulkEditValues[prop.Field] : editValue}
                hasChanged={
                  isBulkEditable && prop.Field
                    ? hasPropertyValueChanged(bulkEditValues[prop.Field], originalBulkValues[prop.Field], prop.Format)
                    : hasPropertyValueChanged(editValue, originalValue, prop.Format)
                }
                isUpdating={isUpdating}
                hideSaveCancel={isBulkEditable}
                onEditChange={isBulkEditable && prop.Field ? (value) => handleBulkEditChange(prop.Field!, value) : setEditValue}
                onSave={isBulkEditable ? handleSaveDialogEdit : () => handleSaveEdit(prop)}
                onCancel={isBulkEditable ? handleCancelDialogEdit : handleCancelEdit}
                onEnterEditMode={
                  isDialogEditMode && component.UpdateData && !prop.UpdateData && !prop.Lookup ? handleOpenBulkEditMode : () => handleEnterEditMode(index, prop)
                }
                onEnterBulkEditMode={!!component.UpdateData && !isBulkEditMode && !isDialogEditMode ? handleOpenBulkEditMode : undefined}
                autoEnterEditMode={!isDialogEditMode && (component.AutoEnterEditMode ?? true)}
              />
            </Stack>
          </DataDisplayPropertyItem>
        );
      })}
    </Grid2>
  );

  return (
    <>
      <Panel
        ref={containerRef}
        headerLabel={headerLabel}
        headerIconName={component.HeaderIconName}
        headerActions={headerActions}
        headerSx={headerLayoutSx}
        compact={component.Compact}
        usePaper={component.UsePaper !== false}
        sx={{ m: 1, ...componentLayoutSx }}
        {...selectionProps}
      >
        {dataPending && <Spinner />}
        {data && renderProperties(false)}
      </Panel>
      <Dialog open={isDialogEditMode && isEditDialogOpen} onClose={handleDialogClose} fullWidth maxWidth="sm">
        <DialogTitle>{headerLabel || "Edit Data"}</DialogTitle>
        <DialogContent dividers>{data && renderProperties(true)}</DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDialogEdit} disabled={isUpdating}>
            Cancel
          </Button>
          <Button onClick={handleSaveDialogEdit} disabled={!hasBulkChanges || isUpdating} loading={isUpdating} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DataDisplayComponent;
